import React, { Fragment, useEffect } from "react";

import Heading from "@common/components/Heading/Heading";
import PageTitle from "@common/components/PageTitle/PageTitle";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import displayToast from "@common/components/Toast/displayToast";
import { isAdmin } from "@common/models/Member.model";
import identifiers from "@common/util/identifiers.json";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import CreditCardDetailsCard from "@main/pages/Administration/components/BillingCards/CreditCardDetailsCard";
import ReceiptsCard from "@main/pages/Administration/components/BillingCards/ReceiptsCard";
import { CustomPricingSubscriptionCard } from "@main/pages/Administration/components/SubscriptionCards/CustomPricingSubscriptionPlan/CustomPricingSubscriptionCard";
import ResumeSubscriptionCard from "@main/pages/Administration/components/SubscriptionCards/ResumeSubscriptionCard/ResumeSubscriptionCard";
import { YourSubscriptionCard } from "@main/pages/Administration/components/SubscriptionCards/YourSubscriptionCard/YourSubscriptionCard";
import { useBillingViewConsumer } from "@main/pages/Administration/consumers/useBillingViewConsumer";
import { useSearchParams } from "react-router-dom";

export default function BillingView(): JSX.Element {
  const { selectedWorkspace } = useAppSelectionContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    customer,
    upcomingAmount,
    globalLoading,
    creditCard,
    updateCreditCard,
    receipts,
    plans,
    addOns,
    isSdkInitializationsLoading,
    isSdkInitializationsError,
    sdkInitializationsData,
    cancelSubscription,
    resumeSubscription,
  } = useBillingViewConsumer();

  useEffect(() => {
    if (searchParams.get("mode") && searchParams.get("success")) {
      if (searchParams.get("mode") === "subscription" && searchParams.get("success") === "true") {
        displayToast({
          title: "Thank you for upgrading",
          content: "You're in — now it's time to make the most out of Shake.",
        });
      } else if (searchParams.get("mode") === "setup" && searchParams.get("success") === "true") {
        displayToast({
          title: "Card updated",
          content: "All the changes have been saved and you're good to go.",
        });
      }
      searchParams.delete("mode");
      searchParams.delete("success");
      setSearchParams(searchParams);
      return;
    }
  }, [searchParams, setSearchParams]);

  return (
    <Fragment>
      <PageTitle>
        <Heading
          as="h1"
          heading1
          marginBottom={8}
          testId={identifiers["billing.header.main"]}
        >
          Billing
        </Heading>
        <p>{"Manage your plan and payment details."}</p>
      </PageTitle>
      {customer && receipts && !globalLoading ? (
        <PageWrap>
          {selectedWorkspace?.is_free ? (
            <CustomPricingSubscriptionCard />
          ) : (
            <YourSubscriptionCard
              customer={customer}
              plans={plans}
              sdkInitializationsData={sdkInitializationsData}
              isSdkInitializationsDataLoading={isSdkInitializationsLoading}
              isSdkInitializationsDataError={isSdkInitializationsError}
              addOns={addOns}
              cancelSubscription={cancelSubscription}
            />
          )}
          {!selectedWorkspace?.is_free && isAdmin(selectedWorkspace?.role) && (
            <Fragment>
              {(selectedWorkspace?.has_subscription || creditCard) && (
                <CreditCardDetailsCard
                  customer={customer}
                  creditCard={creditCard}
                  updateCreditCard={updateCreditCard}
                  upcomingAmount={upcomingAmount}
                />
              )}
              {customer.subscription?.cancel_at_period_end && (
                <ResumeSubscriptionCard
                  subscription={customer.subscription}
                  resumeSubscription={resumeSubscription}
                />
              )}
              <ReceiptsCard
                receipts={receipts}
                customer={customer}
                selectedWorkspace={selectedWorkspace}
              />
            </Fragment>
          )}
        </PageWrap>
      ) : (
        <Spinner />
      )}
    </Fragment>
  );
}
